import {
  readOtherProductTypeAnswers,
  type OtherProductCategory,
} from '@/lib/otherProductTypeStorage';

/** localStorage draft for second “why previous brand” when Q2 = Yes. */
export const OTHER_PREV_BRAND_ANSWER_STORAGE_KEY = 'ldsurvey:otherPrevBrandAnswer';

export type ElaboratedReason = {
  checked: boolean;
  elaboration: string;
};

export const OTHER_RMC_REASON_KEYS = [
  'taste',
  'brandImage',
  'productQuality',
  'price',
  'productFeatures',
  'recommendedByOthers',
] as const;

export const OTHER_VAPE_RRP_REASON_KEYS = [
  'offerMoreFlavours',
  'moreConsiderate',
  'potentialToReduceRisks',
  'recommendedByOthers',
] as const;

export type OtherRmcReasonKey = (typeof OTHER_RMC_REASON_KEYS)[number];
export type OtherVapeRrpReasonKey = (typeof OTHER_VAPE_RRP_REASON_KEYS)[number];
export type OtherPrevBrandReasonKey = OtherRmcReasonKey | OtherVapeRrpReasonKey;

export type OtherPrevBrandAnswerAnswers = {
  [K in OtherPrevBrandReasonKey]: ElaboratedReason;
} & {
  /** How many of 10 smokes are the first (main) product; second = 10 - this. */
  firstProductCount: number;
  /** True once the respondent has moved the smoke-frequency slider. */
  smokeFrequencyAdjusted: boolean;
};

const emptyElaborated = (): ElaboratedReason => ({ checked: false, elaboration: '' });

export const emptyOtherPrevBrandAnswerAnswers = (): OtherPrevBrandAnswerAnswers => ({
  taste: emptyElaborated(),
  brandImage: emptyElaborated(),
  productQuality: emptyElaborated(),
  price: emptyElaborated(),
  productFeatures: emptyElaborated(),
  recommendedByOthers: emptyElaborated(),
  offerMoreFlavours: emptyElaborated(),
  moreConsiderate: emptyElaborated(),
  potentialToReduceRisks: emptyElaborated(),
  firstProductCount: 5,
  smokeFrequencyAdjusted: false,
});

function parseFirstProductCount(value: unknown): number {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > 10) {
    return 5;
  }
  return value;
}

function parseElaborated(value: unknown): ElaboratedReason {
  if (value === true) return { checked: true, elaboration: '' };
  if (value === false || value == null) return emptyElaborated();
  if (typeof value !== 'object') return emptyElaborated();
  const o = value as Record<string, unknown>;
  return {
    checked: o.checked === true,
    elaboration: typeof o.elaboration === 'string' ? o.elaboration : '',
  };
}

export function readOtherPrevBrandAnswerAnswers(): OtherPrevBrandAnswerAnswers {
  try {
    const raw = localStorage.getItem(OTHER_PREV_BRAND_ANSWER_STORAGE_KEY);
    if (!raw) return emptyOtherPrevBrandAnswerAnswers();
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return emptyOtherPrevBrandAnswerAnswers();
    const o = parsed as Record<string, unknown>;
    return {
      taste: parseElaborated(o.taste),
      brandImage: parseElaborated(o.brandImage),
      productQuality: parseElaborated(o.productQuality),
      price: parseElaborated(o.price),
      productFeatures: parseElaborated(o.productFeatures),
      recommendedByOthers: parseElaborated(o.recommendedByOthers),
      offerMoreFlavours: parseElaborated(o.offerMoreFlavours),
      moreConsiderate: parseElaborated(o.moreConsiderate),
      potentialToReduceRisks: parseElaborated(o.potentialToReduceRisks),
      firstProductCount: parseFirstProductCount(o.firstProductCount),
      smokeFrequencyAdjusted: o.smokeFrequencyAdjusted === true,
    };
  } catch {
    return emptyOtherPrevBrandAnswerAnswers();
  }
}

export function writeOtherPrevBrandAnswerAnswers(data: OtherPrevBrandAnswerAnswers): void {
  try {
    localStorage.setItem(OTHER_PREV_BRAND_ANSWER_STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function clearOtherPrevBrandAnswerAnswers(): void {
  try {
    localStorage.removeItem(OTHER_PREV_BRAND_ANSWER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function getOtherPrevBrandReasonKeys(
  category: OtherProductCategory | '' = readOtherProductTypeAnswers().category
): readonly OtherPrevBrandReasonKey[] {
  if (category === 'VAPE' || category === 'RRP') return OTHER_VAPE_RRP_REASON_KEYS;
  return OTHER_RMC_REASON_KEYS;
}

export type OtherPrevBrandReasonType = OtherPrevBrandReasonKey;

export type OtherPrevBrandReasonSelection = {
  type: OtherPrevBrandReasonType;
  elaboration: string;
};

export function isOtherPrevBrandAnswerComplete(): boolean {
  const data = readOtherPrevBrandAnswerAnswers();
  const keys = getOtherPrevBrandReasonKeys();
  if (!keys.some((key) => data[key].checked)) return false;
  if (!data.smokeFrequencyAdjusted) return false;
  return Number.isInteger(data.firstProductCount) && data.firstProductCount >= 0 && data.firstProductCount <= 10;
}

export function getSelectedOtherPrevBrandReasons(
  data: OtherPrevBrandAnswerAnswers,
  category?: OtherProductCategory | ''
): OtherPrevBrandReasonSelection[] {
  const selected: OtherPrevBrandReasonSelection[] = [];
  for (const key of getOtherPrevBrandReasonKeys(category)) {
    if (data[key].checked) {
      selected.push({ type: key, elaboration: data[key].elaboration });
    }
  }
  return selected;
}
